import { unavailableMediaUrl } from "./media-url";
/**
 * 動画メタ（詳細）。1 本の動画について、いま分かっていることを出す。
 *
 * ## 「まだ処理していない」と「処理して何も出なかった」を分ける
 *
 * どちらも区間が 0 件・文字起こしが 0 行になるので、**同じ見た目にしない**。
 * 処理の前は `processGap`（まだ読み取っていない断り）を出し、処理のあとは
 * 「読み取ったが見つからなかった」と書く。見分けは `processState` で付ける。
 *
 * ## この 1 本を処理する
 *
 * 見出しの右に、状態に合った 1 つだけを置く（処理する・もう一度処理する・
 * m3u8 から処理する）。押せない動画ではボタンを出さず、理由を言葉で出す
 * （押しても何も起きない導線を出さない — ADR 0001）。判定は一覧と同じ
 * `processActionFor` にある。押したあとは、始まったことと、取り消しの操作
 * （`JobControls`）を出す。
 *
 * ## m3u8 は再生しない
 *
 * mp4 の原本が無く HLS（m3u8）だけがある動画は、処理はできる（結合してから読む）が、
 * 画面では再生しない。m3u8 をそのまま再生できないブラウザが多いためである。
 *
 * ## 置いていない枠
 *
 * - **空のタイムラインの表**。1 行も入らない表を置かない
 * - **再生数**。動画 1 本に紐づけられる取り込み元が無い
 */

import { useCallback, useRef, useState } from "react";
import type { JobProgressFieldsFragment } from "visumo-data-agent-web-shared";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Flex,
  Image,
  Tag,
} from "antd";
import { Link, useParams } from "react-router";
import { useQuery } from "urql";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  VideoMetaDocDocument,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";
import { JobControls } from "./JobControls";
import {
  processActionFor,
  processLabel,
  VideoProcessConfirm,
} from "./VideoProcessAction";

import { useViewer } from "../ViewerContext";
import { formatJst } from "../format-time";
import { formatDuration, stateLabel, videoTitle } from "./VideoMetaPage";
import { formatClock, VideoSegmentList } from "./VideoSegmentList";
import {
  activeTranscriptIndexAt,
  VideoTranscriptList,
} from "./VideoTranscriptList";

const SOURCE_LABELS: Record<string, string> = {
  OFFICIAL_SNS: "公式 SNS",
  UGC: "UGC",
  REVIEW: "レビュー",
  OTHER: "その他",
};

/** 音声の状態の表示名。**「無音」と「見ていない」を同じ文言にしない**。 */
const AUDIO_STATE_LABELS: Record<string, string> = {
  NOT_CHECKED: "音声はまだ見ていません",
  SILENT: "音声から文字は取れませんでした",
  TRANSCRIBED: "音声から文字起こしが取れました",
};

/** 解像度は両方そろっているときだけ出す（片方だけの数字を並べない）。 */
function formatSize(
  width: number | null,
  height: number | null,
): string | null {
  if (width === null || height === null) return null;
  return `${String(width)} × ${String(height)}`;
}

export function VideoMetaDetailPage() {
  const viewer = useViewer();
  const videoRef = useRef<HTMLVideoElement>(null);
  const pendingSeek = useRef<number | null>(null);
  const [failedVideoUrl, setFailedVideoUrl] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // 文字起こしの「いま」の行。再生位置からだけ決める（押した行ではない）
  const [confirming, setConfirming] = useState(false);
  const [startedJob, setStartedJob] =
    useState<JobProgressFieldsFragment | null>(null);
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState<
    number | null
  >(null);
  const seek = (index: number, seconds: number) => {
    setActiveIndex(index);
    if (!videoRef.current) return;
    try {
      if (videoRef.current.readyState === 0) pendingSeek.current = seconds;
      else videoRef.current.currentTime = seconds;
      void videoRef.current.play().catch(() => {});
    } catch {
      // メタデータの読み込み前は、プレイヤーの標準操作で再生できる。
    }
  };
  const params = useParams<{ videoId: string }>();
  const videoId = params.videoId ?? "";
  const [result, refetch] = useQuery({
    query: VideoMetaDocDocument,
    variables: { id: videoId },
  });

  // 版が食い違ったときに読み直す。**黙って上書きしない**
  const reread = useCallback(() => {
    refetch({ requestPolicy: "network-only" });
  }, [refetch]);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const doc = result.data.videoMetaDoc;
  if (doc === null || doc === undefined) {
    return (
      <div className="vd-page">
        <PageHeader title="動画メタ" />
        <Card title="見つかりません">
          <p aria-label="見つからない理由">
            この動画は取り込めているものの中にありませんでした。
          </p>
          <p style={{ marginTop: 8 }}>
            <Link to="/video-meta">動画の一覧へ戻る</Link>
          </p>
        </Card>
      </div>
    );
  }

  const duration = formatDuration(doc.durationSec ?? null);
  const size = formatSize(doc.width ?? null, doc.height ?? null);
  const posted = formatJst(doc.postedAt ?? null);
  const interpretedAt = formatJst(doc.interpretedAt ?? null);
  const processed = doc.processState === "DONE";

  /**
   * 読み取りの前の断り。
   *
   * **処理の記録（`processNote`）がある動画では出さない。** どちらも出すと
   * 「当面の対象外です／HLS だけで mp4 の原本がありません」の下に
   * 「この動画はまだ処理していません」が並び、読む人には矛盾して見える
   * （2026-09-20 に dev の対象外の動画で実際にそう出ていた）。
   * 記録があるなら、それがいま分かっていることである。
   */
  const gap =
    processed || doc.processNote != null ? null : (doc.processGap ?? null);

  const timelineDuration = Math.max(
    doc.durationSec ?? 0,
    ...doc.segments.map((segment) => segment.endSec),
    1,
  );
  const tickStep = Math.max(1, Math.round(timelineDuration / 8));
  const ticks = Array.from(
    { length: Math.floor(timelineDuration / tickStep) + 1 },
    (_, i) => i * tickStep,
  );
  const mainProducts = doc.mainProducts ?? [
    ...new Set(
      doc.segments.flatMap((segment) =>
        segment.confirmation
          ? [segment.confirmation.productName]
          : segment.products.map((product) => product.label),
      ),
    ),
  ];
  const frames = doc.frames;
  const action = processActionFor(doc);
  const title = videoTitle(doc);

  return (
    <div className="vd-page vd-video-detail">
      <Breadcrumb
        items={[
          { title: <Link to="/video-meta">動画メタ</Link> },
          { title: videoTitle(doc) },
        ]}
      />
      <PageHeader
        title={videoTitle(doc)}
        description="帯または区間の再生ボタンをクリックすると、動画がその秒へ飛びます"
        titleExtra={<HelpIcon slug="video-meta/detail" />}
        extra={
          <span className="vd-row">
            {action.kind === "available" ? (
              startedJob === null && !confirming ? (
                <Button
                  type="primary"
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  {processLabel(action, "detail")}
                </Button>
              ) : null
            ) : (
              <span className="vd-stat-note" aria-label="処理できない理由">
                {action.reason}
              </span>
            )}
            <Link to="/video-meta">一覧へ戻る</Link>
          </span>
        }
      />
      {startedJob !== null ? (
        <div className="vd-stack" style={{ marginBottom: 16 }}>
          <p className="vd-stat-note" role="status" style={{ margin: 0 }}>
            処理を始めました。終わると状態が変わります。この画面を閉じても進みます。
          </p>
          <JobControls
            job={startedJob}
            onChanged={(next) => {
              setStartedJob(next);
            }}
          />
        </div>
      ) : confirming && action.kind === "available" ? (
        <div style={{ marginBottom: 16 }}>
          <VideoProcessConfirm
            videoId={doc.id}
            title={title}
            action={action}
            onStarted={(job) => {
              setStartedJob(job);
              setConfirming(false);
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        </div>
      ) : null}
      <div className="vd-video-columns">
        <div>
          <div className="vd-video-player">
            <Card
              size="small"
              title="動画"
              extra={<HelpIcon slug="video-meta/detail" anchor="動画" />}
            >
              {doc.videoUrl ? (
                <video
                  key={doc.id}
                  ref={videoRef}
                  aria-label="動画プレイヤー"
                  controls
                  preload="none"
                  src={doc.videoUrl}
                  onError={() => setFailedVideoUrl(doc.videoUrl)}
                  onLoadedMetadata={(event) => {
                    if (pendingSeek.current !== null) {
                      event.currentTarget.currentTime = pendingSeek.current;
                      pendingSeek.current = null;
                    }
                    setFailedVideoUrl(null);
                  }}
                  poster={
                    doc.thumbnailUrl && !unavailableMediaUrl(doc.thumbnailUrl)
                      ? doc.thumbnailUrl
                      : undefined
                  }
                  onTimeUpdate={(event) => {
                    const current = event.currentTarget.currentTime;
                    setActiveIndex(
                      doc.segments.find(
                        (segment) =>
                          current >= segment.startSec &&
                          current < segment.endSec,
                      )?.index ?? null,
                    );
                    setActiveTranscriptIndex(
                      activeTranscriptIndexAt(doc.transcript, current),
                    );
                  }}
                />
              ) : (
                <Empty
                  description={
                    doc.hlsUrl
                      ? "mp4 の原本は取り込めていません。処理は m3u8 から行えます"
                      : "mp4 の原本は取り込めていません"
                  }
                />
              )}
              {doc.videoUrl && failedVideoUrl === doc.videoUrl && (
                <Alert
                  type="error"
                  title="動画を読み込めませんでした"
                  description="原本の URL と公開状態を確認してください。読み取り済みのメタ情報は引き続き確認できます。"
                />
              )}
              <Flex gap={12} wrap style={{ marginTop: 8 }}>
                <span aria-label="尺">{duration ?? "取り込めていません"}</span>
                <span aria-label="大きさ">{size ?? "取り込めていません"}</span>
                <Tag color={doc.hasAudio === true ? "blue" : undefined}>
                  {doc.hasAudio == null
                    ? "音声の有無は未記録"
                    : doc.hasAudio
                      ? "音声あり"
                      : "音声なし"}
                </Tag>
              </Flex>
              <div aria-label="動画ファイル" style={{ marginTop: 8 }}>
                {doc.videoUrl ? (
                  <a href={doc.videoUrl} rel="noreferrer" target="_blank">
                    動画ファイルを開く
                  </a>
                ) : (
                  "mp4 の原本は取り込めていません"
                )}
              </div>
            </Card>
          </div>
        </div>
        <div className="vd-video-content">
          <Card
            size="small"
            title="この動画について"
            extra={
              <HelpIcon slug="video-meta/detail" anchor="この動画について" />
            }
          >
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2 }}
              items={[
                { key: "ugc", label: "投稿", children: doc.ugcId },
                {
                  key: "tenant",
                  label: "テナント",
                  children: viewer.tenant.label,
                },
                { key: "seq", label: "seq", children: doc.seq },
                {
                  key: "source",
                  label: "取り込み元",
                  children:
                    doc.sourceType == null
                      ? "記録されていません"
                      : (SOURCE_LABELS[doc.sourceType] ?? doc.sourceType),
                },
                {
                  key: "posted",
                  label: "投稿された時刻",
                  children: (
                    <span aria-label="投稿された時刻">
                      {posted ?? "取り込めていません"}
                    </span>
                  ),
                },
                {
                  key: "state",
                  label: "処理",
                  children: (
                    <strong aria-label="処理の状態">
                      {doc.processState === "SKIPPED"
                        ? "当面の対象外です"
                        : stateLabel(doc.processState)}
                    </strong>
                  ),
                },
              ]}
            />
            {doc.processNote ? (
              <p aria-label="処理の記録">{doc.processNote}</p>
            ) : null}
            {gap ? (
              <p className="vd-stat-note">
                {gap.why}
                <br />
                出せるようになるために要るもの: {gap.needs}
              </p>
            ) : null}
            {processed ? (
              <>
                <p aria-label="要約">
                  {doc.summary ?? "読み取りましたが、要約は残りませんでした。"}
                </p>
                <Flex gap={8} wrap align="center">
                  <span className="vd-stat-note">主商品</span>
                  {mainProducts.length
                    ? mainProducts.map((name) => (
                        <Tag key={name} color="#EDAA86">
                          {name}
                        </Tag>
                      ))
                    : "この動画からは特定できていません"}
                </Flex>
                <Flex className="vd-video-meta-row" gap={8} wrap align="center">
                  <span className="vd-stat-note">主話題</span>
                  {doc.mainTopics == null ? (
                    <span className="vd-stat-note">
                      話題は記録されていません
                    </span>
                  ) : doc.mainTopics.length === 0 ? (
                    "特定できていません"
                  ) : (
                    doc.mainTopics.map((topic) => (
                      <Tag key={topic}>{topic}</Tag>
                    ))
                  )}
                </Flex>
                <Flex className="vd-video-meta-row" gap={8} wrap align="center">
                  <span className="vd-stat-note">人物</span>
                  <span>
                    {doc.peopleSummary ?? "人物の要約は記録されていません"}
                  </span>
                </Flex>
                <p className="vd-stat-note">
                  {interpretedAt
                    ? `読み取り ${interpretedAt}`
                    : "読み取った時刻は残っていません。"}
                </p>
              </>
            ) : null}
          </Card>
          {processed ? (
            <>
              <Card
                size="small"
                title={`タイムライン（${doc.segments.length} segment）`}
                extra={
                  <HelpIcon slug="video-meta/detail" anchor="タイムライン" />
                }
              >
                <div aria-label="タイムライン">
                  {doc.segments.length === 0 ? (
                    <p>読み取りましたが、区間は 1 つも出ませんでした。</p>
                  ) : (
                    <>
                      <div className="vd-video-ticks">
                        {ticks.map((time) => (
                          <span
                            key={time}
                            style={{
                              left: `${(time / timelineDuration) * 100}%`,
                            }}
                          >
                            {time}s
                          </span>
                        ))}
                      </div>
                      <div className="vd-video-timeline">
                        {doc.segments.map((segment) => (
                          <button
                            key={segment.index}
                            type="button"
                            aria-label={`segment ${segment.index + 1}（${segment.startSec} 秒から）へシーク`}
                            aria-pressed={activeIndex === segment.index}
                            disabled={!doc.videoUrl}
                            onClick={() =>
                              seek(segment.index, segment.startSec)
                            }
                            style={{
                              left: `${(segment.startSec / timelineDuration) * 100}%`,
                              width: `${((segment.endSec - segment.startSec) / timelineDuration) * 100}%`,
                            }}
                          >
                            {segment.index + 1}
                          </button>
                        ))}
                      </div>
                      <VideoSegmentList
                        onConflict={reread}
                        segments={doc.segments}
                        videoId={doc.id}
                        activeIndex={activeIndex}
                        onSeek={doc.videoUrl ? seek : undefined}
                      />
                    </>
                  )}
                </div>
              </Card>
              <Card
                size="small"
                title="文字起こし"
                extra={
                  <HelpIcon slug="video-meta/detail" anchor="文字起こし" />
                }
              >
                <p className="vd-stat-note">
                  {AUDIO_STATE_LABELS[doc.audioState] ?? doc.audioState}
                </p>
                {doc.audioSummary ? <p>{doc.audioSummary}</p> : null}
                {doc.transcript.length === 0 ? (
                  <p aria-label="文字起こし">
                    文字起こしの行は残っていません。
                  </p>
                ) : (
                  <VideoTranscriptList
                    lines={doc.transcript}
                    // 再生していない動画では、どの行も「いま」にしない
                    activeIndex={doc.videoUrl ? activeTranscriptIndex : null}
                    onSeek={
                      doc.videoUrl
                        ? (seconds) => {
                            seek(-1, seconds);
                          }
                        : undefined
                    }
                  />
                )}
              </Card>
              <Card
                size="small"
                title="根拠フレーム"
                extra={
                  <HelpIcon slug="video-meta/detail" anchor="根拠フレーム" />
                }
              >
                {frames == null ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="この読み取りでは根拠画像の保存先が記録されていません"
                  />
                ) : frames.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="根拠フレームはありません"
                  />
                ) : (
                  <Image.PreviewGroup>
                    <Flex gap={10} wrap>
                      {frames.map((frame) => {
                        const segments = doc.segments.filter((segment) =>
                          segment.evidence.some(
                            (evidence) =>
                              evidence.kind === "FRAME" &&
                              evidence.index === frame.index,
                          ),
                        );
                        const caption = segments.length
                          ? segments
                              .map((segment) => `#${segment.index + 1}`)
                              .join(" / ")
                          : `フレーム ${frame.index + 1}`;
                        return (
                          <figure key={frame.index} className="vd-video-frame">
                            <Image
                              src={frame.url}
                              alt={`${caption} の根拠フレーム`}
                              width={104}
                              height={138}
                              style={{ objectFit: "cover", borderRadius: 4 }}
                            />
                            <figcaption>
                              {caption} ·{" "}
                              <button
                                type="button"
                                disabled={!doc.videoUrl}
                                onClick={() =>
                                  seek(segments[0]?.index ?? -1, frame.atSec)
                                }
                                aria-label={`根拠フレーム ${frame.index + 1} の時刻へシーク`}
                              >
                                {formatClock(frame.atSec)}
                              </button>
                            </figcaption>
                          </figure>
                        );
                      })}
                    </Flex>
                    <p className="vd-stat-note">
                      画像をクリックすると拡大します。時刻をクリックすると動画の該当箇所へ移動します。
                    </p>
                  </Image.PreviewGroup>
                )}
              </Card>
              <Collapse
                items={[
                  {
                    key: "raw",
                    label: "生 JSON を開く",
                    children: (
                      <pre className="vd-video-json">
                        {JSON.stringify(doc, null, 2)}
                      </pre>
                    ),
                  },
                ]}
              />
            </>
          ) : null}
          <Link to="/video-meta">動画の一覧へ戻る</Link>
        </div>
      </div>
    </div>
  );
}
